import { IconUsers } from '@tabler/icons-react';
import React from 'react';
import { Trans } from 'react-i18next';

import { roundTo2 } from '@Root/src/helpers/utils';

import TypingTestResultRankingTestsTakenModel from '@Api/models/TypingTestResultRankingTestsTakenModel';

import { Icon } from '@Components/atoms/Icon/Icon';
import ProgressBar from '@Components/atoms/ProgressBar';
import { RowGap } from '@Components/atoms/Row/Row';
import Skeleton from '@Components/atoms/Skeleton';
import Text from '@Components/atoms/Text';
import { Tooltip, TooltipContent } from '@Components/atoms/Tooltip/Tooltip';
import { PropsWithClassName } from '@Components/helper';
import { getStringWithLength } from '@Components/helper/utils';
import UserLink from '@Components/molecules/UserLink/UserLink';

import { TextAlign, TextColor, TextSize } from '@Helpers/types/text';

import { iconStroke } from '@Assets/styles/theme/theme';

import {
  IconContainer,
  MainContainerRow,
  PositionText,
  PrimaryText,
  Root,
  SecondaryText,
  StyledTooltipTrigger,
  StyledTrophyIcon,
} from './TypingTestHighscoreTestsTakenRank.styles';

interface Props extends PropsWithClassName {
  result: TypingTestResultRankingTestsTakenModel;
  position: number;
  interval?: string;
}

const TypingTestHighscoreTestsTakenRank = (props: Props): React.ReactElement => {
  const { result, position, interval } = props;

  const user = result.user;
  const percent = result.percent;

  const renderGroupTooltip = () => (
    <Text size={TextSize.Medium}>
      <Trans
        i18nKey="highscore.rank.tests_taken.group_tooltip"
        ns="typing-test"
        values={{ percent: roundTo2(percent ?? 0), testsTakenGroup: result.testsTakenGroup, interval }}
        count={result.testsTakenGroup}
        components={{ bold: <strong /> }}
      />
    </Text>
  );

  const renderPosition = () => {
    const rank = result.rank ?? position;

    if (rank > 3) {
      return (
        <PositionText size={TextSize.Medium} bold color={TextColor.Tertiary}>
          {rank}
        </PositionText>
      );
    }

    return <StyledTrophyIcon rank={rank} />;
  };

  return (
    <Root
      isHighlighted={result.isCurrentUser}
      className={props.className + ' Rank'}
      data-testid={props.dataTestId ?? 'typing-test-highscore-tests-taken-rank-root'}
    >
      {renderPosition()}
      {user && <UserLink user={user} />}
      {percent && (
        <Tooltip>
          <StyledTooltipTrigger>
            <IconContainer>
              <Icon icon={IconUsers} size={26} stroke={iconStroke} />
            </IconContainer>
            <MainContainerRow noGutters>
              <ProgressBar progress={percent} />
            </MainContainerRow>
            <SecondaryText size={TextSize.Xs} textAlign={TextAlign.Right} color={TextColor.Tertiary}>
              {percent >= 100 ? 100 : roundTo2(percent)}%
            </SecondaryText>
          </StyledTooltipTrigger>
          <TooltipContent>{renderGroupTooltip()}</TooltipContent>
        </Tooltip>
      )}
      <PrimaryText bold size={TextSize.Xs} textAlign={TextAlign.Right}>
        {result.testsTakenGroup}
      </PrimaryText>
    </Root>
  );
};

const SkeletonComponent = (props: PropsWithClassName): React.ReactElement => {
  return (
    <Root
      isHighlighted={false}
      className={props.className + ' Rank'}
      data-testid={props.dataTestId ?? 'typing-test-highscore-tests-taken-rank-skeleton-root'}
    >
      <Skeleton>
        <PositionText size={TextSize.Medium} bold>
          {getStringWithLength(2)}
        </PositionText>
      </Skeleton>
      <Skeleton width="30px" height="30px" round />
      <MainContainerRow noGutters rowGap={RowGap.Small}>
        <Skeleton>
          <Text size={TextSize.Small}>{getStringWithLength(25)}</Text>
        </Skeleton>
      </MainContainerRow>
      <Skeleton>
        <SecondaryText bold size={TextSize.Xs} textAlign={TextAlign.Right}>
          {getStringWithLength(2)}
        </SecondaryText>
      </Skeleton>
      <Skeleton>
        <PrimaryText bold size={TextSize.Xs} textAlign={TextAlign.Right}>
          {getStringWithLength(2)}
        </PrimaryText>
      </Skeleton>
    </Root>
  );
};

TypingTestHighscoreTestsTakenRank.Skeleton = SkeletonComponent;

export default TypingTestHighscoreTestsTakenRank;
